import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user) return <Navigate to="/dashboard" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/dashboard");
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
            <div className="flex flex-col p-8 sm:p-12 lg:p-16">
                <Link to="/" className="flex items-center gap-2.5 mb-12 w-fit" data-testid="login-logo-link">
                    <div className="h-8 w-8 bg-brand text-white grid place-items-center font-heading font-bold tracking-tight rounded-sm">
                        A
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-slate-900 text-base">Allied Pod</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Barbados</div>
                    </div>
                </Link>
                <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-3">Welcome back</p>
                    <h1 className="font-heading text-4xl tracking-tight font-bold text-slate-900">Sign in to your account.</h1>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">Manage your credits, view bookings, and reserve a pod.</p>
                    <form onSubmit={submit} className="mt-10 space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                data-testid="login-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-[0.16em] font-mono text-slate-600">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                data-testid="login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 h-11 bg-slate-50 border-slate-200 rounded-md focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={loading}
                            className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-md group"
                        >
                            {loading ? "Signing in…" : (
                                <>
                                    Sign in
                                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                </>
                            )}
                        </Button>
                        <div className="text-center">
                            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-slate-500 hover:text-brand transition-colors underline-offset-4 hover:underline">
                                Forgot your password?
                            </Link>
                        </div>
                    </form>
                    <p className="mt-8 text-sm text-slate-600">
                        New to Allied Pod?{" "}
                        <Link to="/register" data-testid="login-to-register-link" className="text-brand font-medium hover:underline underline-offset-4">
                            Create an account →
                        </Link>
                    </p>
                    <div className="mt-6 pt-6 border-t border-slate-200">
                        <Link to="/admin-login" data-testid="login-to-admin-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-mono text-slate-500 hover:text-slate-900 transition-colors group">
                            <span className="h-1.5 w-1.5 bg-amber-500 rounded-full" />
                            Admin sign-in
                            <span className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
                        </Link>
                    </div>
                </div>
            </div>
            <div className="hidden lg:block relative overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1760611656615-db3fad24a314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBwb2R8ZW58MHx8fHwxNzc4MDExOTUwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Modern office space"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 text-white">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/80 mb-3">A consulting space</p>
                    <h2 className="font-heading text-3xl tracking-tight font-bold leading-tight max-w-md">"It feels like having an office in Bridgetown — without the lease."</h2>
                </div>
            </div>
        </div>
    );
}
